import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from './useApiQuery';
import { useAuthContext } from '../contexts/AuthContext';
import type {
  JobListItem,
  JobsResponse,
  JobsScope,
  JobStatusCounts,
} from '../types/jobs';
import type { JobStatus } from '../types/monitoring';

interface UseJobsResult {
  jobs: JobListItem[];
  /** A request for data the grid does not have yet (first load, new filter). */
  isLoading: boolean;
  /** Any request, including a quiet refresh of what is already shown. */
  isFetching: boolean;
  /** Rows and counts are the previous filter's while this one loads. */
  isPlaceholder: boolean;
  hasData: boolean;
  error: Error | null;
  total: number;
  statusCounts: JobStatusCounts;
  /** Re-read this view. */
  refresh: () => void;
  /** Re-read every jobs view (after a stop changes run state). */
  refetch: () => void;
}

interface UseJobsOptions {
  scope: JobsScope;
  createdBy?: string;
  /** Up to 100 IDs; an empty selection disables the request. */
  jobIds?: readonly string[];
  enabled?: boolean;
  page?: number;
  limit?: number;
  search?: string;
  status?: JobStatus | null;
  waiting?: boolean;
}

const EMPTY_JOBS: JobListItem[] = [];
const EMPTY_COUNTS: JobStatusCounts = {};

export function useJobs(options: UseJobsOptions): UseJobsResult {
  const {
    scope,
    createdBy,
    jobIds,
    enabled = true,
    page = 1,
    limit = 50,
    search = '',
    status = null,
    waiting = false,
  } = options;
  const { user } = useAuthContext();
  const invalidateJobs = useInvalidateJobs();
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  const creator = createdBy ?? (scope === 'mine' ? user?.sub : undefined);
  if (creator) params.set('created_by', creator);
  for (const id of [...new Set(jobIds)].sort()) params.append('job_ids', id);
  if (search.trim()) params.set('search', search.trim());
  if (status) params.set('status', status);
  if (waiting) params.set('waiting', '1');
  const canFetch = enabled && (jobIds === undefined || jobIds.length > 0);
  const query = useApiQuery<JobsResponse>(`/meta/jobs?${params}`, canFetch);
  const data = canFetch ? query.data : undefined;
  // Stop/Force stop use this callback too: invalidate the other filter views
  // so switching filters cannot resurrect a fresh cache of the old run state.
  const refetch = invalidateJobs;

  const { refetch: refetchQuery } = query;
  const refresh = useCallback(() => {
    if (canFetch) void refetchQuery();
  }, [canFetch, refetchQuery]);

  return {
    jobs: data?.items ?? EMPTY_JOBS,
    total: data?.total ?? 0,
    statusCounts: data?.status_counts ?? EMPTY_COUNTS,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholder: query.isPlaceholderData,
    hasData: data !== undefined,
    error: query.error,
    refresh,
    refetch,
  };
}

/**
 * Mark every cached jobs view stale, refetching the ones on screen. Anything
 * that changes run state (Stop, Force stop) calls this, wherever it lives, so
 * returning to Jobs never shows the old state as fresh.
 */
export function useInvalidateJobs(): () => void {
  const { user } = useAuthContext();
  const client = useQueryClient();
  const caller = user?.sub ?? null;
  return useCallback(() => {
    void client.invalidateQueries({ queryKey: ['api', caller, '/meta/jobs'] });
  }, [client, caller]);
}
